/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {dlsColor} from '../../theme/theme';
import {IconProps} from '../../types/types';
import useIconDLS from '../useIconDLS';

// the class names of the icon's layers, for styling its states from outside ...
export interface MaterialRadioButtonIconClasses {
  ring?: string;
  dot?: string;
  ripple?: string;
  halo?: string;
}

export interface MaterialRadioButtonIconProps extends IconProps {
  checked?: boolean;
  className?: string;
  classes?: MaterialRadioButtonIconClasses;
}

// kept out of the component so the style object keeps a stable identity ...
const iconStyle = {
  display    : 'block',
  overflow   : 'visible',
  flexShrink : 0
} as const;

/**
 * Defines the Design Language System (DLS) Material Radio Button Icon component.
 *
 * It is drawn as vector shapes (the Figma radio asset) rather than with CSS
 * borders, which snap to whole device pixels and so thin out, and push the dot
 * off centre, at a browser zoom or display scale other than 100%. Its layers
 * - the ripple and focus halo (both hidden until a state shows them), the
 * ring, and the dot of a checked icon - take the resting colors of the default
 * state, and can be restyled per state through `classes`.
 *
 * ```typescript
 * import MaterialRadioButtonIcon from '@ion/dls/src/icons/current/MaterialRadioButtonIcon';
 * ...
 *
 * <MaterialRadioButtonIcon checked classes={{ring: classes.ring}} />
 * ```
 */
export const MaterialRadioButtonIcon = ({
  checked = false,
  className,
  classes,
  ...props
}: MaterialRadioButtonIconProps) => {
  const {iconSize} = useIconDLS(props);
  return (
    <svg width={iconSize}
      height={iconSize}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
      focusable='false'
      className={className}
      style={iconStyle}>
      <circle className={classes?.ripple}
        cx='12'
        cy='12'
        r='12'
        fill={dlsColor('aquamarine')}
        opacity='0' />
      <circle className={classes?.halo}
        cx='12'
        cy='12'
        r='11'
        stroke={dlsColor('skyBlue')}
        strokeWidth='2'
        opacity='0' />
      <circle className={classes?.ring}
        cx='12'
        cy='12'
        r='9'
        stroke={dlsColor(checked ? 'aaBlue' : 'grey2')}
        strokeWidth='2' />
      {checked && (
        <circle className={classes?.dot}
          cx='12'
          cy='12'
          r='5'
          fill={dlsColor('aaBlue')} />
      )}
    </svg>
  );
};

export default React.memo(MaterialRadioButtonIcon);
